import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import i18nStore from "../../../../i18n/store";
import {
    GridActionsCellItem,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridRowId,
    GridRowsProp
} from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ControlPointDuplicateOutlinedIcon from '@mui/icons-material/ControlPointDuplicateOutlined';
import Tooltip from '@mui/material/Tooltip';
import { CreateOrUpdateEquipmentsInterface, EquipmentsInterface } from "../../schemas/equipments";
import CustomEditNumberComponent from "../cellRenderers/CustomEditNumberComponent";
import CustomRenderCell from "../cellRenderers/CustomRenderCell";
import CustomRenderXRGBCell from "../cellRenderers/CustomRenderXRGBCell";
import IsActiveField from "../cellRenderers/IsActiveField";
import SelectNodeEditComponent from "../SelectNodeEditComponent";


export const useMuiEquipmentsColDefs = (
    rows: GridRowsProp,
    setRows: (rows: GridRowsProp) => void,
    handlerDeleteClick: (id: GridRowId) => void,
    handlerDuplicateClick: (row: any) => void,
    updateApiFunc: (projectId: string | number, equipment: CreateOrUpdateEquipmentsInterface) => Promise<EquipmentsInterface>,
): GridColDef[] => {
    const translations = i18nStore.getTranslations;
    const [colDefs, setColDefs] = useState<GridColDef[]>([]);

    const handleDeleteClick = (id: GridRowId) => () => {
        handlerDeleteClick(id);
    };

    const handleDuplicateClick = (id: GridRowId) => () => {
        const duplicatedRow = rows.find((row) => row.id === id);
        handlerDuplicateClick(duplicatedRow);
    };

    useEffect(() => {
        const newDefColsEquipment: GridColDef[] = [
            {
                field: "id",
                type: 'number',
            },
            {
                field: "is_active",
                type: 'boolean',                
                renderHeader: () => (
                    <Tooltip title={translations._active.isActiveLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._active.isActive}
                        </Typography>
                    </Tooltip>
                ),
                editable: false,
                widthPart: 0.8,
                filter: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <IsActiveField {...params} rows={rows} setRows={setRows} updateApiFunc={updateApiFunc} />
                },
            },
            {
                field: "name",                
                renderHeader: () => (
                    <Tooltip title={translations.nameLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations.name}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                widthPart: 3,
                headerAlign: 'center',
            },
            {
                field: "node_id",                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.nodeLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.node}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                    return <SelectNodeEditComponent
                        {...params}
                        rows={rows}
                        setRows={setRows}
                        updateApiFunc={updateApiFunc}
                    />
                },
            },
            {
                field: "R1",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.r1Long} placement="top">
                        <Typography variant="subtitle2">
                            R1, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderCell {...params} realValue={params.row.R1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "X1",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.x1Long} placement="top">
                        <Typography variant="subtitle2">
                            X1, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    return <CustomRenderXRGBCell {...params} realValue={params.row.X1} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
            {
                field: "R2",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.r2Long} placement="top">
                        <Typography variant="subtitle2">
                            R2, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    if (!params.value && !params.row.X2) {
                        value = params.row.R1;
                    }

                    return <CustomRenderCell {...params} value={value} realValue={params.row.R2} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "X2",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.x2Long} placement="top">
                        <Typography variant="subtitle2">
                            X2, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    if (!params.row.R2 && !params.row.X2) {
                        value = params.row.X1;
                    }
                    return <CustomRenderXRGBCell {...params} value={value} realValue={params.row.X2} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
            {
                field: "R0",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.r0Long} placement="top">
                        <Typography variant="subtitle2">
                            R0, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    if (!params.row.R0 && !params.row.X0) {
                        value = params.row.R1;
                    }
                    return <CustomRenderCell {...params} value={value} realValue={params.row.R0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={0} />;
                },
            },
            {
                field: "X0",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.x0Long} placement="top">
                        <Typography variant="subtitle2">
                            X0, {translations._measUnit.Om}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    if (!params.row.R0 && !params.row.X0) {
                        value = params.row.X1;
                    }
                    return <CustomRenderXRGBCell {...params} value={value} realValue={params.row.X0} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} />;
                },
            },
            {
                field: "EMF",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.EMFModuleLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.EMF} {translations._tablesBranchesEquip.mod}, {translations._measUnit.kV}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    return <CustomRenderCell {...params} value={value} realValue={params.row.EMF} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} g={0} />;
                },
            },
            {
                field: "EMF_ANGLE",
                type: 'number',                
                renderHeader: () => (
                    <Tooltip title={translations._tablesBranchesEquip.EMFAngleLong} placement="top">
                        <Typography variant="subtitle2">
                            {translations._tablesBranchesEquip.EMF} {translations._tablesBranchesEquip.angle}, {translations._measUnit.grad}
                        </Typography>
                    </Tooltip>
                ),
                editable: true,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<any, number>) => {
                    let value = params.value || 0;
                    return <CustomRenderCell {...params} value={value} realValue={params.row.EMF_ANGLE} />;
                },
                renderEditCell: (params: GridRenderEditCellParams) => {
                    return <CustomEditNumberComponent {...params} ge={-360} le={360} />;
                },
            },
            {
                field: "actions",
                type: 'actions',
                headerName: "",
                sort: false,
                widthPart: 0.8,
                headerAlign: 'center',
                cellClassName: 'actions',
                getActions: ({ id }): any[] => {
                    return [
                        <GridActionsCellItem
                            icon={
                                <Tooltip title={translations.duplicate} placement="top">
                                    <ControlPointDuplicateOutlinedIcon />
                                </Tooltip>
                            }
                            label={translations.duplicate}
                            className="textPrimary"
                            onClick={handleDuplicateClick(id)}
                            color="inherit"
                        />,
                        <GridActionsCellItem
                            icon={
                                <Tooltip title={translations.delete} placement="top">
                                    <DeleteIcon />
                                </Tooltip>
                            }
                            label={translations.delete}
                            onClick={handleDeleteClick(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ] as GridColDef[];

        setColDefs(newDefColsEquipment);
    }, [i18nStore.getCurrentLanguageCode, rows]);

    return colDefs;
}
